import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  Handshake,
  Image as ImagemIcone,
  MoreHorizontal,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Star,
  UserPlus,
} from 'lucide-react';
import { useState } from 'react';

import {
  getParceirosAdminControllerListarQueryKey,
  useParceirosAdminControllerDefinirAtivo,
  useParceirosAdminControllerListar,
  useParceirosAdminControllerReordenar,
} from '@/api/generated/admin-parceiros/admin-parceiros';
import type { ParceiroAdminResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { ImagensDoParceiroDialogo } from '@/features/admin/parceiros/components/ImagensDoParceiroDialogo';
import { ParceiroFormDialogo } from '@/features/admin/parceiros/components/ParceiroFormDialogo';
import { UsuarioDoParceiroDialogo } from '@/features/admin/parceiros/components/UsuarioDoParceiroDialogo';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Input } from '@/shared/ui/input';
import { Paginacao } from '@/shared/ui/pagination';
import { Skeleton } from '@/shared/ui/skeleton';
import { toast } from '@/shared/ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

type Dialogo = 'form' | 'imagens' | 'usuario';

/**
 * Administração de parceiros (B8-4/B8-5).
 *
 * ⚠️ **Sem excluir**: parceiro é ambiente, e o ambiente tem 19 relações em
 * cascata. "Remover da vitrine" é desativar.
 *
 * ⚠️ A reordenação é por **setas**, não arrasto. A V1 usava `@dnd-kit`
 * (3 pacotes) e enviava N updates soltos; aqui o endpoint é transacional e as
 * setas resolvem o mesmo problema sem dependência nova e sem excluir quem
 * navega por teclado.
 */
export function ParceirosAdminPage() {
  const { pode } = usePermissoes();
  const queryClient = useQueryClient();

  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [dialogo, setDialogo] = useState<Dialogo | null>(null);
  const [selecionado, setSelecionado] = useState<ParceiroAdminResponse | undefined>();

  const { data, isPending, isError, refetch } = useParceirosAdminControllerListar({
    pagina,
    porPagina: 20,
    ordenarPor: 'ordem',
    ordem: 'asc',
    ...(busca.trim() ? { busca: busca.trim() } : {}),
  });

  const definirAtivo = useParceirosAdminControllerDefinirAtivo();
  const reordenar = useParceirosAdminControllerReordenar();

  const podeCriar = pode('admin_parceiros.criar');
  const podeEditar = pode('admin_parceiros.editar');

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: getParceirosAdminControllerListarQueryKey() });
  }

  function abrir(qual: Dialogo, parceiro?: ParceiroAdminResponse) {
    setSelecionado(parceiro);
    setDialogo(qual);
  }

  function alternarAtivo(parceiro: ParceiroAdminResponse) {
    definirAtivo.mutate(
      { id: parceiro.id, data: { ativo: !parceiro.ativo } },
      {
        onSuccess: () => {
          invalidar();
          toast.success(parceiro.ativo ? 'Parceiro fora da vitrine' : 'Parceiro na vitrine');
        },
        onError: (erro) =>
          toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível alterar'),
      },
    );
  }

  /** Troca de posição com o vizinho e manda a lista inteira da página. */
  function mover(indice: number, direcao: -1 | 1) {
    if (!data) return;
    const destino = indice + direcao;
    if (destino < 0 || destino >= data.itens.length) return;

    const ids = data.itens.map((p) => p.id);
    [ids[indice], ids[destino]] = [ids[destino]!, ids[indice]!];

    reordenar.mutate(
      { data: { ids } },
      {
        onSuccess: invalidar,
        onError: (erro) =>
          toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível reordenar'),
      },
    );
  }

  if (isError) return <EstadoErro aoTentarDeNovo={() => void refetch()} />;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Handshake className="w-7 h-7 text-brand" />
            Parceiros
          </h2>
          <p className="text-muted-foreground mt-1">
            Cadastro, logo, ordem na vitrine e acesso do parceiro.
          </p>
        </div>

        {podeCriar && (
          <Button
            onClick={() => abrir('form')}
            className="gap-2 bg-brand text-brand-foreground hover:bg-brand/90"
          >
            <Plus className="w-4 h-4" />
            Novo parceiro
          </Button>
        )}
      </div>

      <Input
        placeholder="Buscar pelo nome..."
        value={busca}
        onChange={(e) => {
          setBusca(e.target.value);
          setPagina(1);
        }}
        className="max-w-sm"
      />

      {isPending ? (
        <Skeleton className="h-72 w-full rounded-xl" />
      ) : data.itens.length === 0 ? (
        <EstadoVazio
          icone={Handshake}
          titulo="Nenhum parceiro"
          descricao={busca ? 'Nada encontrado com esse termo.' : 'Cadastre o primeiro parceiro.'}
        />
      ) : (
        <>
          <div className="rounded-xl border border-border overflow-hidden bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">Ordem</TableHead>
                  <TableHead>Parceiro</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead className="text-center">Produtos</TableHead>
                  <TableHead className="text-center">Pendentes</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-20">Ações</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {data.itens.map((parceiro, indice) => (
                  <TableRow key={parceiro.id}>
                    <TableCell>
                      <div className="flex items-center gap-0.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          aria-label="Subir"
                          disabled={!podeEditar || indice === 0 || reordenar.isPending}
                          onClick={() => mover(indice, -1)}
                        >
                          <ArrowUp className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          aria-label="Descer"
                          disabled={
                            !podeEditar || indice === data.itens.length - 1 || reordenar.isPending
                          }
                          onClick={() => mover(indice, 1)}
                        >
                          <ArrowDown className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>

                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-8 rounded bg-white border border-border flex items-center justify-center shrink-0 overflow-hidden">
                          {parceiro.logoUrl ? (
                            <img
                              src={parceiro.logoUrl}
                              alt=""
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <Handshake className="w-4 h-4 text-muted-foreground/40" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-foreground truncate">{parceiro.nome}</p>
                          {parceiro.mostrarNaSidebar && (
                            <span className="inline-flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400">
                              <Star className="w-3 h-3 fill-current" />
                              No carrossel
                            </span>
                          )}
                        </div>
                      </div>
                    </TableCell>

                    <TableCell>
                      {parceiro.categoria ? (
                        <Badge variant="secondary">{parceiro.categoria}</Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>

                    <TableCell className="text-center">{parceiro.totalProdutos}</TableCell>

                    <TableCell className="text-center">
                      {parceiro.totalMateriaisPendentes > 0 ? (
                        <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200">
                          {parceiro.totalMateriaisPendentes}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>

                    <TableCell>
                      <Situacao parceiro={parceiro} />
                    </TableCell>

                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" aria-label="Ações">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {podeEditar && (
                            <>
                              <DropdownMenuItem onClick={() => abrir('form', parceiro)}>
                                <Pencil className="w-4 h-4 mr-2" />
                                Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => abrir('imagens', parceiro)}>
                                <ImagemIcone className="w-4 h-4 mr-2" />
                                Logo
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => alternarAtivo(parceiro)}>
                                {parceiro.ativo ? (
                                  <>
                                    <PowerOff className="w-4 h-4 mr-2" />
                                    Tirar da vitrine
                                  </>
                                ) : (
                                  <>
                                    <Power className="w-4 h-4 mr-2" />
                                    Devolver à vitrine
                                  </>
                                )}
                              </DropdownMenuItem>
                            </>
                          )}
                          {podeCriar && (
                            <DropdownMenuItem onClick={() => abrir('usuario', parceiro)}>
                              <UserPlus className="w-4 h-4 mr-2" />
                              Criar usuário
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <Paginacao
            pagina={data.pagina}
            totalPaginas={data.totalPaginas}
            total={data.total}
            porPagina={data.porPagina}
            aoMudarPagina={setPagina}
          />
        </>
      )}

      {dialogo === 'form' && (
        <ParceiroFormDialogo
          aberto
          aoFechar={() => setDialogo(null)}
          {...(selecionado ? { parceiro: selecionado } : {})}
        />
      )}
      {dialogo === 'imagens' && selecionado && (
        <ImagensDoParceiroDialogo aberto aoFechar={() => setDialogo(null)} parceiro={selecionado} />
      )}
      {dialogo === 'usuario' && selecionado && (
        <UsuarioDoParceiroDialogo aberto aoFechar={() => setDialogo(null)} parceiro={selecionado} />
      )}
    </div>
  );
}

/**
 * Perfil e ambiente são interruptores diferentes: o admin precisa distinguir
 * "tirei da vitrine" de "o ambiente inteiro foi desativado", porque o segundo
 * derruba os usuários do parceiro e não se resolve nesta tela.
 */
function Situacao({ parceiro }: { parceiro: ParceiroAdminResponse }) {
  if (!parceiro.tenantAtivo) {
    return (
      <Badge variant="secondary" className="gap-1">
        Ambiente desativado
      </Badge>
    );
  }

  return parceiro.ativo ? (
    <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
      Na vitrine
    </Badge>
  ) : (
    <Badge variant="secondary">Fora da vitrine</Badge>
  );
}
